'use client';
import React, { useEffect, useState } from 'react';
import {
    useTheme, TableSortLabel, Select, MenuItem, Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    InputAdornment, Avatar, Chip, Pagination, Card, CardContent, Divider
} from '@mui/material';
import { Helmet } from 'react-helmet';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import { useUser } from "../context/UserContext";
import SectionLoader from '../components/SectionLoader';
import ConfirmDialog from "../components/ConfirmDialog";
import Toast from "../components/Toast";
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArchiveManager from "../utils/archiveManager";

const Groups = () => {
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');
    const [groups, setGroups] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [editGroup, setEditGroup] = useState(null);
    const { user } = useUser();
    const [selectedGroupForView, setSelectedGroupForView] = useState(null);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [addGroup, setAddGroup] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [draftsOpen, setDraftsOpen] = useState(false);
    const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('groups'));
    const [activeDraftId, setActiveDraftId] = useState(null);
    const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('groups'));
    const [filterActiveTab, setFilterActiveTab] = useState('all');
    const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Auto-save draft on beforeunload
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (groupName.trim() && addGroup) {
                DraftManager.saveDraft('groups', { groupName }, activeDraftId);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [groupName, addGroup, activeDraftId]);

    const handleManualSaveDraft = () => {
        if (!groupName.trim()) {
            setToast({ open: true, message: "Please enter a group name before saving draft.", severity: "warning" });
            return;
        }
        const res = DraftManager.saveDraft('groups', { groupName }, activeDraftId);
        if (res.success) {
            setActiveDraftId(res.draftId);
            setDraftsList(res.drafts || []);
            setToast({ open: true, message: "Group draft saved successfully!", severity: "success" });
        } else {
            setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
        }
    };

    const handleContinueDraft = (draft) => {
        if (!draft || !draft.data) return;
        if (draft.data.groupName !== undefined) setGroupName(draft.data.groupName);
        setActiveDraftId(draft.id);
        setAddGroup(true);
        setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
    };

    const handleDeleteDraft = (draftId) => {
        const res = DraftManager.deleteDraft('groups', draftId);
        setDraftsList(res.drafts || []);
        if (activeDraftId === draftId) setActiveDraftId(null);
        setToast({ open: true, message: "Draft removed.", severity: "info" });
    };

    const handleArchive = (groupItem) => {
        setConfirmDialog({
            open: true,
            title: "Archive Group",
            message: "Are you sure you want to archive this group? It will no longer be visible to mobile app users until restored.",
            onConfirm: () => {
                ArchiveManager.archiveItem('groups', groupItem);
                setArchivedList(ArchiveManager.getArchivedItems('groups'));
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                setToast({ open: true, message: "Group archived and hidden from mobile app.", severity: "info" });
            }
        });
    };

    const handleRestore = (groupId) => {
        ArchiveManager.restoreItem('groups', groupId);
        setArchivedList(ArchiveManager.getArchivedItems('groups'));
        setToast({ open: true, message: "Group restored to active status!", severity: "success" });
    };

    const handlePermanentDelete = (groupId) => {
        setConfirmDialog({
            open: true,
            title: "Permanently Delete Group",
            message: "Are you sure you want to permanently delete this archived group? This cannot be undone.",
            onConfirm: () => {
                ArchiveManager.deletePermanently('groups', groupId);
                setArchivedList(ArchiveManager.getArchivedItems('groups'));
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                setToast({ open: true, message: "Group permanently removed.", severity: "success" });
            }
        });
    };

    useEffect(() => {
        fetchGroups();
    }, [page, search]);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}groups`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (data.success) {
                let filteredGroups = data.groups;
                if (search) {
                    filteredGroups = filteredGroups.filter(g =>
                        g.name.toLowerCase().includes(search.toLowerCase())
                    );
                }
                setTotalPages(Math.ceil(filteredGroups.length / pageSize));
                const paginatedGroups = filteredGroups.slice((page - 1) * pageSize, page * pageSize);
                setGroups(paginatedGroups);
            }
        } catch (err) {
            setToast({ open: true, message: "Failed to fetch groups", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (group) => {
        setEditGroup(group);
        setGroupName(group.name);
    };

    const handleDelete = async (id) => {
        setConfirmDialog({
            open: true,
            title: 'Delete Group',
            message: 'Are you sure you want to delete this group? This action cannot be undone.',
            onConfirm: async () => {
                setConfirmDialog({ ...confirmDialog, open: false });
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_URL}delete-group`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ id: id })
                    });
                    const data = await response.json();
                    if (response.ok && data.success) {
                        setToast({ open: true, message: "Group deleted successfully", severity: "success" });
                        fetchGroups();
                    } else {
                        setToast({ open: true, message: "Delete failed: " + (data?.message || 'Unknown error'), severity: "error" });
                    }
                } catch (err) {
                    setToast({ open: true, message: "Network error", severity: "error" });
                }
            }
        });
    };

    const handleSaveEdit = async () => {
        if (!groupName.trim()) {
            setToast({ open: true, message: 'Group name is required', severity: "error" });
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}update-group`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: editGroup.id,
                    name: groupName.trim(),
                })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setToast({ open: true, message: "Group updated successfully", severity: "success" });
                setEditGroup(null);
                setGroupName('');
                fetchGroups();
            } else {
                setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
            }
        } catch (err) {
            setToast({ open: true, message: "Network error", severity: "error" });
        }
    };

    const handleSaveAdd = async () => {
        if (!groupName.trim()) {
            setToast({ open: true, message: 'Group name is required', severity: "error" });
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}add-group`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: groupName.trim(),
                })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setToast({ open: true, message: "Group added successfully", severity: "success" });
                if (activeDraftId) {
                    DraftManager.deleteDraft('groups', activeDraftId);
                    setActiveDraftId(null);
                    setDraftsList(DraftManager.getDrafts('groups'));
                }
                setAddGroup(false);
                setGroupName('');
                fetchGroups();
            } else {
                setToast({ open: true, message: data?.message || 'Unknown error', severity: "error" });
            }
        } catch (err) {
            setToast({ open: true, message: "Network error", severity: "error" });
        }
    };

    const displayedGroups = filterActiveTab === "archived"
        ? archivedList
        : groups.filter(g => !archivedList.some(a => String(a.id) === String(g.id)));

    return (
        <>
            <Helmet>
                <title>Groups - Glocal Prayer Network</title>
            </Helmet>
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{
                    mb: { xs: 2, sm: 3 },
                    pb: { xs: 1.5, sm: 2.5 },
                    pt: { xs: 1, sm: 2 },
                    background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.15) 0%, rgba(44, 62, 80, 0.15) 100%)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 2,
                    border: '1px solid rgba(76, 161, 175, 0.3)',
                    boxShadow: '0 4px 12px rgba(76, 161, 175, 0.2)',
                    px: { xs: 2, sm: 3 }
                }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 0.5, letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        Group Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Manage groups available for organization members.
                    </Typography>
                </Box>

                <Paper sx={{
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                            Groups List
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                            <TextField
                                size="small"
                                placeholder="Search groups"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ width: { xs: '100%', sm: 300 } }}
                            />
                            <Button
                                variant={filterActiveTab === "archived" ? "contained" : "outlined"}
                                startIcon={<InventoryIcon />}
                                onClick={() => setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived")}
                                sx={{
                                    color: filterActiveTab === "archived" ? '#fff' : '#64d2e2',
                                    borderColor: 'rgba(76, 161, 175, 0.4)',
                                    backgroundColor: filterActiveTab === "archived" ? '#177373' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: filterActiveTab === "archived" ? '#135e5e' : 'rgba(76, 161, 175, 0.15)',
                                    }
                                }}
                            >
                                Archived ({archivedList.length})
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DraftsIcon />}
                                onClick={() => {
                                    setDraftsList(DraftManager.getDrafts('groups'));
                                    setDraftsOpen(true);
                                }}
                                sx={{
                                    borderColor: 'rgba(255, 183, 77, 0.5)',
                                    color: '#ffb74d',
                                    '&:hover': {
                                        borderColor: '#ffb74d',
                                        backgroundColor: 'rgba(255, 183, 77, 0.1)',
                                    }
                                }}
                            >
                                Drafts ({draftsList.length}/3)
                            </Button>
                            {user?.role === 1 && (
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    onClick={() => { setAddGroup(true); setGroupName(''); }}
                                    sx={{ background: '#177373', '&:hover': { background: '#146363' } }}
                                >
                                    Add Group
                                </Button>
                            )}


                        </Box>
                    </Box>
                </Paper>

                <Paper sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    {/* Desktop Table View */}
                    <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                    <TableSortLabel
                                        active={true}
                                        direction={sortOrder}
                                        onClick={() => {
                                            const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                                            setSortOrder(newOrder);
                                            setGroups(prev => [...prev].sort((a, b) => {
                                                const nameA = (a.name || '').toLowerCase();
                                                const nameB = (b.name || '').toLowerCase();
                                                return newOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                                            }));
                                        }}
                                    >
                                        Group Name
                                    </TableSortLabel>
                                </TableCell>
                                    <TableCell>Members Count</TableCell>
                                    <TableCell>Created At</TableCell>
                                    {user?.role === 1 && <TableCell>Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ position: "relative", height: 200 }}>
                                            <SectionLoader open={loading} height={200} />
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && displayedGroups.map((group) => (
                                    <TableRow
                                        key={group.id}
                                        hover
                                        onClick={() => {
                                            setSelectedGroupForView(group);
                                            setGroupModalOpen(true);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'background-color 0.15s ease',
                                            '&:hover': {
                                                bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                                            },
                                            '& td': {
                                                borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                                                py: 1.5,
                                                color: isLight ? '#1E293B' : '#F1F5F9',
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Avatar sx={{ bgcolor: '#4CA1AF', fontWeight: 700 }}>{group.name ? group.name[0].toUpperCase() : 'G'}</Avatar>
                                                {group.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${group._count?.group_users || 0} Members`} size="small" variant="outlined" sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.3)' }} />
                                        </TableCell>
                                        <TableCell>
                                            {group.created_at ? new Date(group.created_at).toLocaleDateString('en-GB') : 'N/A'}
                                        </TableCell>
                                        {filterActiveTab === "archived" ? (
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Restore to active (Make visible in mobile app)">
                                                    <IconButton onClick={() => handleRestore(group.id)} sx={{ color: '#4CAF50' }}>
                                                        <UnarchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Permanently delete">
                                                    <IconButton onClick={() => handlePermanentDelete(group.id)} sx={{ color: '#EF4444' }}>
                                                        <DeleteForeverIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        ) : user?.role === 1 ? (
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Edit Group">
                                                    <IconButton onClick={() => handleEdit(group)} sx={{ color: '#4CA1AF' }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Archive (Hide from mobile app)">
                                                    <IconButton onClick={() => handleArchive(group)} sx={{ color: '#ffb74d' }}>
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Group">
                                                    <IconButton onClick={() => handleDelete(group.id)} sx={{ color: '#EF4444' }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                                {!loading && displayedGroups.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'rgba(255,255,255,0.5)' }}>
                                            {filterActiveTab === "archived" ? "No archived groups found." : "No groups found."}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {displayedGroups.length > 0 ? (
                                displayedGroups.map((group) => (
                                    <Card
                                        key={group.id}
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedGroupForView(group);
                                            setGroupModalOpen(true);
                                        }}
                                        sx={{ 
                                            borderRadius: 2, 
                                            background: 'rgba(255,255,255,0.03)',
                                            borderColor: 'rgba(255,255,255,0.1)',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s ease',
                                            '&:hover': { transform: 'translateY(-2px)' }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Avatar sx={{ bgcolor: '#4CA1AF', fontWeight: 700, borderRadius: 2 }}>{group.name ? group.name[0].toUpperCase() : 'G'}</Avatar>
                                                <Box>
                                                    <Typography fontWeight={700} color="#FFFFFF">{group.name}</Typography>
                                                    <Typography variant="caption" color="rgba(255,255,255,0.6)">Group</Typography>
                                                </Box>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Box>
                                                    <Typography variant="caption" color="rgba(255,255,255,0.5)" display="block">Members</Typography>
                                                    <Chip label={`${group._count?.group_users || 0} Members`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }} />
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" color="rgba(255,255,255,0.5)" display="block">Created</Typography>
                                                    <Typography variant="body2" color="#FFFFFF" fontWeight={600}>{group.created_at ? new Date(group.created_at).toLocaleDateString('en-GB') : 'N/A'}</Typography>
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                                            
                                            {filterActiveTab === "archived" ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Tooltip title="Restore">
                                                        <IconButton size="small" sx={{ color: '#4CAF50' }} onClick={() => handleRestore(group.id)}>
                                                            <UnarchiveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Permanently">
                                                        <IconButton size="small" sx={{ color: '#EF4444' }} onClick={() => handlePermanentDelete(group.id)}>
                                                            <DeleteForeverIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : user?.role === 1 ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <IconButton size="small" sx={{ color: '#4CA1AF' }} onClick={() => handleEdit(group)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" sx={{ color: '#ffb74d' }} onClick={() => handleArchive(group)}>
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" sx={{ color: '#EF4444' }} onClick={() => handleDelete(group.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ) : null}
                                        </CardContent>
                                    </Card>
                                ))
                            ) : (
                                <Typography align="center" sx={{ py: 4, color: 'rgba(255,255,255,0.5)' }}>
                                    {filterActiveTab === "archived" ? "No archived groups found" : "No groups found"}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} />
                    </Box>
                )}

                {/* Add/Edit Dialog */}
                <Dialog open={addGroup || !!editGroup} onClose={() => { setAddGroup(false); setEditGroup(null); }} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{editGroup ? 'Edit Group' : 'Add New Group'}</span>
                        {!editGroup && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DraftsIcon />}
                                    onClick={() => {
                                        setDraftsList(DraftManager.getDrafts('groups'));
                                        setDraftsOpen(true);
                                    }}
                                    sx={{
                                        borderColor: 'rgba(255, 183, 77, 0.5)',
                                        color: '#ffb74d',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Drafts ({draftsList.length}/3)
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleManualSaveDraft}
                                    sx={{
                                        borderColor: 'rgba(76, 161, 175, 0.5)',
                                        color: '#64d2e2',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Save Draft
                                </Button>
                            </Box>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Group Name"
                                    variant="outlined"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                />
                                <HelpTooltip
                                    title="Group Name"
                                    purpose="The public title of this fellowship or prayer group"
                                    howItWorks="Displayed in the community group list and search filters"
                                    mobileEffect="Members see this name in the mobile app when joining or posting"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                                <Button onClick={() => { setAddGroup(false); setEditGroup(null); }}>Cancel</Button>
                                <Button variant="contained" onClick={editGroup ? handleSaveEdit : handleSaveAdd} sx={{ background: '#177373' }}>
                                    {editGroup ? 'Save Changes' : 'Add Group'}
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Group Detail Modal Popup */}
                <Dialog
                    open={groupModalOpen}
                    onClose={() => {
                        setGroupModalOpen(false);
                        setSelectedGroupForView(null);
                    }}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            bgcolor: isLight ? '#FFFFFF' : '#1e293b',
                            backgroundImage: 'none',
                            overflow: 'hidden',
                        }
                    }}
                >
                    {selectedGroupForView && (
                        <>
                            <DialogTitle
                                sx={{
                                    p: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                                    Group Information
                                </Typography>
                                <IconButton
                                    onClick={() => {
                                        setGroupModalOpen(false);
                                        setSelectedGroupForView(null);
                                    }}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>

                            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                                        borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            bgcolor: '#4CA1AF',
                                            color: '#FFF',
                                            fontWeight: 800,
                                            fontSize: 24,
                                        }}
                                    >
                                        {selectedGroupForView.name ? selectedGroupForView.name[0].toUpperCase() : 'G'}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                                            {selectedGroupForView.name}
                                        </Typography>
                                        <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                            <Chip
                                                icon={<PeopleIcon sx={{ fontSize: '15px !important' }} />}
                                                label={`${selectedGroupForView._count?.group_users || 0} Active Members`}
                                                size="small"
                                                color="primary"
                                                sx={{ fontWeight: 600 }}
                                            />
                                            {selectedGroupForView.created_at && (
                                                <Chip
                                                    label={`Created: ${new Date(selectedGroupForView.created_at).toLocaleDateString('en-GB')}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 500 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                                        borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                        Group Summary & Visibility
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
                                        Events and private prayers targeted to <strong>{selectedGroupForView.name}</strong> are exclusively visible to members of this group in the GPN mobile application.
                                    </Typography>
                                </Paper>
                            </DialogContent>

                            <DialogActions sx={{ p: 2, px: 3, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)', gap: 1 }}>
                                {user?.role === 1 && (
                                    <Button
                                        onClick={() => {
                                            setGroupModalOpen(false);
                                            handleEdit(selectedGroupForView);
                                        }}
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        size="small"
                                        sx={{ bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' }, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Edit Group
                                    </Button>
                                )}
                                <Button
                                    onClick={() => {
                                        setGroupModalOpen(false);
                                        setSelectedGroupForView(null);
                                    }}
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                >
                                    Close
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </Box>

            <Toast toast={toast} setToast={setToast} />
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
                severity="error"
            />
            {/* Floating Settings Icon */}
            <Tooltip title="Groups Settings" placement="left">
                <IconButton
                    onClick={() => setSettingsOpen(true)}
                    sx={{
                        position: 'fixed', bottom: 32, right: 32,
                        bgcolor: 'rgba(76, 161, 175, 0.15)', border: '1px solid rgba(76, 161, 175, 0.4)',
                        color: '#64d2e2', width: 48, height: 48, zIndex: 1300, backdropFilter: 'blur(8px)',
                        '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
                        transition: 'all 0.25s ease',
                    }}
                >
                    <SettingsIcon />
                </IconButton>
            </Tooltip>
            <ModuleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} moduleKey="groups" moduleTitle="Groups" />
            <DraftsModal
                open={draftsOpen}
                onClose={() => setDraftsOpen(false)}
                moduleKey="groups"
                moduleTitle="Groups"
                onContinueDraft={handleContinueDraft}
            />
        </>
    );
};

export default Groups;
